// Раздел «Квоты»: распределение квот по пользователям поверх карты промрайонов.
//
// Источник — приказы Росрыболовства (№ 780 с правками, № 784, № 785 и прошлые
// годы), прочитанные со сканов и проверенные гейтом (`src/quota/`). Документ
// публичный, поэтому пользователи названы поимённо. Вылова здесь нет: освоение
// компании — сведения о чужом промысле (`src/cabinet/quotas.py`).
//
// Район на карте заливается тоннами квоты выбранного вида; клик по району —
// список пользователей. Карта своя (как у «Правил»), поэтому рубежи — общим
// хуком и общими тумблерами (по умолчанию выключены); районы с квотой
// обведены своим контуром слоя квоты (`quotaLayer.ts`).

import { domainMaxBounds } from '../domain/limits'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import maplibregl from 'maplibre-gl'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import type { BBox, QuotaKind } from '../api'
import { baseStyle } from '../map/baseStyle'
import { registerBasemapMap } from '../map/basemapRefresh'
import { createStyleGate } from '../map/styleGate'
import { useMapView } from '../map/mapView'
import { BasemapAttribution } from '../map/attribution/BasemapAttribution'
import { BoundaryToggles } from '../map/BoundaryToggles'
import { MapGraticule } from '../map/MapGraticule'
import { useFloatingPanel } from '../map/floatingPanel'
import { useWide } from '../map/useWide'
import { boxKey, boundsToBox, useBoundaryOverlays } from '../map/useBoundaryOverlays'
import { formatDay, formatNumber } from '../i18n/format'
import { gapsLine } from './chainGaps'
import { useLang, useT } from '../i18n'
import { useRegionScope } from '../species/useRegionScope'
import type { MapView } from '../map/mapView'
import { QUOTA_FILL, applyQuotaLayer, shadeDistricts } from './quotaLayer'
import { QUOTAS_URL_KEYS, parseQuotasUrl, writeQuotasUrl } from './quotasUrl'
import type { QuotasUrlState } from './quotasUrl'
import { SpeciesField, SpeciesList } from './SpeciesPicker'
import { SharesPanel, TacPanel } from './QuotaPanels'
import { ContractHistory } from './ContractHistory'
import { QUOTAS_VIEWS } from './quotasUrl'

const HOME_CENTER: [number, number] = [150, 52]
const HOME_ZOOM = 3.4
const KINDS: QuotaKind[] = ['industrial', 'coastal', 'crab', 'invest']
/** Отступ домашнего места панели от угла карты (`left-3 top-3`). */
const PANEL_HOME = { x: 12, y: 12 }

export function QuotasPage() {
  const scope = useRegionScope()
  if (!scope.known) return <div className="h-full w-full" />
  return <QuotasPageInner home={scope.home} />
}

function QuotasPageInner({ home }: { home: MapView | null }) {
  const t = useT()
  const { lang } = useLang()
  const tons = (v: number | null | undefined) => formatNumber(lang, v, v != null && v < 10 ? 3 : 1)
  const [searchParams, setSearchParams] = useSearchParams()
  const [state, setState] = useState<QuotasUrlState>(() => parseQuotasUrl(searchParams))
  const patch = (p: Partial<QuotasUrlState>) => setState(s => ({ ...s, ...p }))
  const boundaries = { eez: state.eez, districts: state.fd }

  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      for (const k of QUOTAS_URL_KEYS) next.delete(k)
      writeQuotasUrl(state).forEach((v, k) => next.set(k, v))
      return next
    }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const query = useQuery({
    queryKey: ['quotas', state.basin, state.year, state.species, state.zone],
    queryFn: () => cabinetApi.quotas(state),
    placeholderData: keepPreviousData,
  })
  const data = query.data
  // Годы вкладки ОДУ свои — их приносит сама вкладка.
  const [tacYears, setTacYears] = useState<{ years: number[]; year: number | null }>({ years: [], year: null })
  const years = state.view === 'tac' ? tacYears.years : state.view === 'shares' ? [] : (data?.years ?? [])
  const currentYear = state.view === 'tac' ? tacYears.year : data?.year
  const zones = data?.zones ?? []
  const zone = zones.find(z => z.code === state.zone) ?? null
  // Пользователь, чьи договоры раскрыты (история держателей по годам).
  const [openInn, setOpenInn] = useState<string | null>(null)

  // ---- карта -------------------------------------------------------------
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  // Карта СОСТОЯНИЕМ — для сетки: ссылка не перерисовывает компонент.
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null)
  const gateRef = useRef(createStyleGate())
  const { center, zoom, onMoveEnd } = useMapView()
  const moveEndRef = useRef(onMoveEnd)
  moveEndRef.current = onMoveEnd
  const initialView = useRef({
    center: center ?? (home?.lng != null && home.lat != null ? [home.lng, home.lat] as [number, number] : HOME_CENTER),
    zoom: zoom ?? home?.zoom ?? HOME_ZOOM,
  })
  const [box, setBox] = useState<BBox | null>(null)
  const [mapZoom, setMapZoom] = useState(initialView.current.zoom)
  const boundaryMeta = useBoundaryOverlays({
    mapRef, gateRef, on: boundaries, viewKey: boxKey(box), beforeId: QUOTA_FILL,
  })
  const pickRef = useRef<(zone: number) => void>(() => {})
  pickRef.current = z => patch({ zone: z })

  useEffect(() => {
    if (!containerRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      attributionControl: false,
      center: initialView.current.center,
      zoom: initialView.current.zoom,
      // Рамка домена: карта не уводится туда, где сервер откажет.
      maxBounds: domainMaxBounds(),
    })
    mapRef.current = map
    setMapInstance(map)
    const gate = createStyleGate()
    gateRef.current = gate
    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
    map.keyboard.disableRotation()
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.on('moveend', () => {
      const c = map.getCenter()
      moveEndRef.current({ lng: c.lng, lat: c.lat, zoom: map.getZoom() })
      setBox(boundsToBox(map))
      setMapZoom(map.getZoom())
    })
    map.on('load', () => {
      applyQuotaLayer(map, { type: 'FeatureCollection', features: [] })
      setBox(boundsToBox(map))
      gate.markReady()
    })
    map.on('click', QUOTA_FILL, e => {
      const z = e.features?.[0]?.properties?.zone
      if (typeof z === 'number') pickRef.current(z)
    })
    map.on('mouseenter', QUOTA_FILL, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', QUOTA_FILL, () => { map.getCanvas().style.cursor = '' })
    const unregisterBasemap = registerBasemapMap(map)
    return () => {
      unregisterBasemap()
      map.remove()
      mapRef.current = null
      setMapInstance(null)
    }
  }, [])

  const districtsQuery = useQuery({
    queryKey: ['quotas', 'districts', boxKey(box), Math.round(mapZoom)],
    queryFn: () => cabinetApi.fishingDistricts({ bbox: box!, zoom: mapZoom }),
    enabled: box != null,
    placeholderData: keepPreviousData,
  })
  const tonsByZone = useMemo(() => new Map(zones.map(z => [z.code, z.tons])), [zones])
  const shaded = useMemo(
    () => shadeDistricts(districtsQuery.data, data?.districts ?? {}, tonsByZone, state.zone),
    [districtsQuery.data, data?.districts, tonsByZone, state.zone],
  )
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => applyQuotaLayer(map, shaded))
  }, [shaded])

  // Районы, у которых на карте нет листа (Чукотское море и т. п.), есть в
  // списке с пометкой: молча пропавший район читался бы как «квоты нет».
  const drawn = useMemo(() => new Set(Object.values(data?.districts ?? {}).flat()), [data?.districts])

  const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar'
  // На телефоне панель — нижний лист, её не тащат.
  const wide = useWide()
  const panel = useFloatingPanel({ storageKey: 'quotas.panel', home: PANEL_HOME, enabled: wide })
  const [picking, setPicking] = useState(false)
  const [speciesQuery, setSpeciesQuery] = useState('')
  const closePicker = () => { setPicking(false); setSpeciesQuery('') }
  // Новый вид — новые районы: выбранный район прежнего вида не переносится.
  const pickSpecies = (code: number | null) => { patch({ species: code, zone: null }); closePicker() }

  return (
    <div className="relative h-full w-full">
      {/* Высота, а НЕ `absolute inset-0` — сторож `mapContainer.test.ts`. */}
      <div ref={containerRef} className="h-full w-full" />
      <MapGraticule map={mapInstance} />
      <BasemapAttribution corner="br" />

      <aside ref={panel.panelRef} style={panel.style}
        className={`absolute left-3 top-3 z-10 flex max-h-[calc(100%-24px)] w-[380px] max-w-[calc(100%-24px)] flex-col overflow-hidden rounded-xl border border-hull bg-abyss/95 shadow-console backdrop-blur max-md:bottom-0 max-md:left-0 max-md:top-auto max-md:max-h-[60%] max-md:w-full max-md:max-w-full max-md:rounded-b-none ${panel.dragging ? 'select-none' : ''}`}>
        <div className="space-y-2.5 border-b border-hull px-3 py-3">
          <div {...panel.handleProps} tabIndex={wide ? 0 : undefined}
            aria-label={wide ? t('quotas.move') : undefined} title={wide ? t('quotas.move') : undefined}
            className={`-mx-3 -mt-3 flex items-center gap-2 px-3 pt-3 ${focusRing} `
              + (wide ? (panel.dragging ? 'cursor-grabbing' : 'cursor-grab') : '')}
            style={wide ? { touchAction: 'none' } : undefined}>
            {wide && (
              <svg aria-hidden viewBox="0 0 8 14" className="h-3.5 w-2 shrink-0 text-mist">
                {[2, 7, 12].flatMap(y => [1.5, 6.5].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2" fill="currentColor" />))}
              </svg>
            )}
            <h1 data-panel-text="" className="min-w-0 cursor-text select-text font-heading text-[15px] font-semibold text-foam">{t('quotas.title')}</h1>
            <span aria-hidden className="min-w-2 flex-1 self-stretch" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select aria-label={t('quotas.basin')} value={state.basin}
              onChange={e => patch({ basin: e.target.value, year: null, species: null, zone: null })}
              className={`rounded-lg border border-hull bg-deep px-2 py-1 text-[12px] text-foam ${focusRing}`}>
              {Array.from(new Set([state.basin, ...(data?.basins ?? [])])).map(b => (
                <option key={b} value={b}>{t(`quotas.basinName.${b}`)}</option>
              ))}
            </select>
            {years.length > 0 && (
              <select aria-label={t('quotas.year')} value={currentYear ?? ''}
                onChange={e => patch({ year: Number(e.target.value), zone: null })}
                className={`tnum rounded-lg border border-hull bg-deep px-2 py-1 text-[12px] text-foam ${focusRing}`}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            <div role="tablist" aria-label={t('quotas.view')} className="flex rounded-lg border border-hull bg-deep p-0.5">
              {QUOTAS_VIEWS.map(v => (
                <button key={v} type="button" role="tab" aria-selected={state.view === v}
                  onClick={() => patch({ view: v, year: null, zone: null })}
                  className={`rounded-md px-2 py-0.5 text-[12px] ${focusRing} `
                    + (state.view === v ? 'bg-sonar/20 text-foam' : 'text-mist hover:text-foam')}>
                  {t(`quotas.viewName.${v}`)}
                </button>
              ))}
            </div>
          </div>
          {state.view === 'alloc' && <SpeciesField species={data?.species ?? []} value={state.species} open={picking}
            query={speciesQuery} onQuery={setSpeciesQuery}
            onOpen={() => setPicking(true)} onClose={closePicker} onPick={pickSpecies} tons={tons} />}
          {state.view === 'alloc' && !picking && (
            <p className="tnum text-[12px] text-mist">
              {t('quotas.total')}: <span className="whitespace-nowrap text-foam">{tons(data?.total_tons)} {t('quotas.t')}</span>
            </p>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {state.view === 'shares' ? <SharesPanel key={state.basin} basin={state.basin} />
          : state.view === 'tac' ? (
            <TacPanel basin={state.basin} year={state.year}
              onYears={(ys, y) => setTacYears({ years: ys, year: y })} />
          ) : <>
          {query.isError && <p className="px-3 py-2 text-[12px] text-coral">{t('quotas.error')}</p>}
          {!query.isError && data && zones.length === 0 && (
            <p className="px-3 py-2 text-[12px] text-mist">{t('quotas.empty')}</p>
          )}

          {picking ? (
            <SpeciesList species={data?.species ?? []} value={state.species}
              query={speciesQuery} onPick={pickSpecies} tons={tons} />
          ) : zone ? (
            <div className="px-3 py-2">
              <button type="button" onClick={() => patch({ zone: null })}
                className="mb-2 text-[12px] text-mist hover:text-foam focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar">
                ← {t('quotas.allZones')}
              </button>
              <h2 className="text-[13px] font-semibold text-foam">{zone.name}{zone.code > 0 ? ` (${zone.code})` : ''}</h2>
              <p className="tnum mb-2 whitespace-nowrap text-[12px] text-mist">
                {tons(zone.tons)} {t('quotas.t')} · {t('quotas.holdersCount', { count: zone.holders })}
              </p>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-mist">
                    <th className="py-1 font-normal">{t('quotas.holder')}</th>
                    <th className="py-1 text-right font-normal">{t('quotas.t')}</th>
                    <th className="py-1 text-right font-normal">%</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.holders ?? []).map(h => (
                    <tr key={h.inn ?? h.name} className="border-t border-hull align-top">
                      <td className="py-1 pr-2 text-foam">
                        {h.inn ? (
                          <button type="button" aria-expanded={openInn === h.inn}
                            onClick={() => setOpenInn(openInn === h.inn ? null : h.inn)}
                            className="text-left hover:text-sonar focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar">
                            {h.name}
                          </button>
                        ) : h.name}
                        <div className="flex flex-wrap gap-x-2 text-[11px] text-mist">
                          {h.inn && <span className="whitespace-nowrap">{t('quotas.inn')} {h.inn}</span>}
                          {KINDS.filter(k => h.kinds[k]).map(k => (
                            <span key={k} className="whitespace-nowrap">{t(`quotas.kind.${k}`)}</span>
                          ))}
                        </div>
                        {h.inn && openInn === h.inn && currentYear != null && (
                          <ContractHistory inn={h.inn} year={currentYear} species={state.species}
                            zone={state.zone} />
                        )}
                      </td>
                      <td className="tnum whitespace-nowrap py-1 text-right text-foam">{tons(h.tons)}</td>
                      <td className="tnum w-10 whitespace-nowrap py-1 pl-2 text-right text-mist">
                        {h.share_of_zone != null ? formatNumber(lang, h.share_of_zone * 100, 1) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <ul>
              {zones.map(z => (
                <li key={z.code}>
                  <button type="button" onClick={() => patch({ zone: z.code })}
                    className="flex w-full items-baseline gap-2 border-b border-hull px-3 py-2 text-left hover:bg-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar">
                    <span className="min-w-0 flex-1 text-[12px] text-foam">
                      {z.name}{z.code > 0 ? ` (${z.code})` : ''}
                      {!drawn.has(z.code) && (
                        <span className="ml-1 text-[11px] text-mist">· {t('quotas.noMap')}</span>
                      )}
                      <span className="flex flex-wrap gap-x-2 text-[11px] text-mist">
                        <span className="whitespace-nowrap">{t('quotas.holdersCount', { count: z.holders })}</span>
                        {KINDS.filter(k => z.kinds[k]).map(k => (
                          <span key={k} className="whitespace-nowrap">{t(`quotas.kind.${k}`)} {tons(z.kinds[k])}</span>
                        ))}
                      </span>
                      {(['industrial', 'coastal'] as const).filter(k => z.tac?.[k]).map(k => (
                        <span key={k} className="block text-[11px] text-mist">
                          {t('quotas.tacCheck', {
                            kind: t(`quotas.kind.${k}`), tac: tons(z.tac?.[k]),
                            pct: formatNumber(lang, ((z.kinds[k] ?? 0) / (z.tac?.[k] ?? 1)) * 100, 0),
                          })}
                        </span>
                      ))}
                    </span>
                    <span className="tnum shrink-0 whitespace-nowrap text-[12px] text-foam">{tons(z.tons)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          </>}
        </div>

        {state.view === 'alloc' && data?.orders?.length ? (
          <footer className="border-t border-hull px-3 py-2 text-[11px] text-mist">
            {t('quotas.source')}:{' '}
            {data.orders.map(o => t('quotas.order', { no: o.no, date: formatDay(lang, o.date), edition: o.edition })).join('; ')}
            {data.orders.filter(o => o.gaps?.length).map(o => (
              <span key={`${o.no}-${o.date}`} className="mt-1 block text-buoy">
                {t('quotas.incomplete', { no: o.no, missing: gapsLine(o.gaps ?? [], lang, t) })}
              </span>
            ))}
          </footer>
        ) : null}
      </aside>

      <div className="pointer-events-none absolute bottom-8 right-3 z-10 max-md:bottom-auto max-md:top-24">
        <div className="pointer-events-auto rounded-xl border border-hull bg-abyss/95 px-3 py-2 text-[12px] shadow-console backdrop-blur">
          <BoundaryToggles on={boundaries}
            onToggle={k => (k === 'eez' ? patch({ eez: !state.eez }) : patch({ fd: !state.fd }))}
            meta={boundaryMeta} />
        </div>
      </div>
    </div>
  )
}
